import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Real table semantics — `<table>`, `<caption>`, scoped headers (docs/04 §6). A grid of
 * divs reads as one long line to a screen reader, which is not acceptable for a ledger.
 */
export const Table = React.forwardRef(({ className, containerClassName, ...props }, ref) => (
  <div className={cn('relative w-full overflow-auto scrollbar-thin', containerClassName)}>
    <table ref={ref} className={cn('w-full caption-bottom text-table', className)} {...props} />
  </div>
));
Table.displayName = 'Table';

export const TableHeader = React.forwardRef(({ className, sticky = true, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      '[&_tr]:border-b bg-muted/50',
      sticky && 'sticky top-0 z-10 backdrop-blur supports-[backdrop-filter]:bg-muted/70',
      className,
    )}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

export const TableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />
));
TableBody.displayName = 'TableBody';

/** The totals row: semibold, top border, sticky at the bottom of the grid (docs/04 §4). */
export const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('sticky bottom-0 z-10 border-t-2 bg-card font-semibold [&>tr]:last:border-b-0', className)}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

export const TableRow = React.forwardRef(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'border-b border-border/60 transition-colors duration-150 hover:bg-muted/50 data-[state=selected]:bg-muted',
      className,
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

export const TableHead = React.forwardRef(({ className, numeric = false, ...props }, ref) => (
  <th
    ref={ref}
    scope="col"
    className={cn(
      'h-11 px-4 text-start align-middle text-caption font-semibold uppercase tracking-wide text-muted-foreground',
      numeric && 'text-end',
      className,
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

export const TableCell = React.forwardRef(({ className, numeric = false, ...props }, ref) => (
  <td
    ref={ref}
    className={cn('px-4 py-3 align-middle', numeric && 'text-end font-mono tabular', className)}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

export const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn('mt-4 text-caption text-muted-foreground', className)} {...props} />
));
TableCaption.displayName = 'TableCaption';
